/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

const SCOPES = [
    { value: 'SELF', label: 'Self (Own Records Only)' },
    { value: 'GROUP', label: 'Group (Same Group)' },
    { value: 'HIERARCHICAL', label: 'Hierarchical (Group + Descendants)' },
    { value: 'GLOBAL', label: 'Global (All Records in Table)' },
];

export default function UserGroupAssignmentModal({ applicationId, user, open, onClose }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [groups, setGroups] = useState([]);
    const [appTables, setAppTables] = useState([]);

    // Form State
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedScope, setSelectedScope] = useState('SELF');
    const [appliesToAll, setAppliesToAll] = useState(true);
    const [allowedTables, setAllowedTables] = useState([]); // List of IDs
    const [discardedTables, setDiscardedTables] = useState([]); // List of IDs
    const [editingId, setEditingId] = useState(null);
    const [tableSearch, setTableSearch] = useState('');

    const listContainerRef = useRef(null);
    const formSectionRef = useRef(null);
    const topRef = useRef(null);
    const shouldScrollToTop = useRef(false);

    useEffect(() => {
        if (shouldScrollToTop.current) {
            // Desktop: Reset inner list scroll
            if (listContainerRef.current) {
                listContainerRef.current.scrollTop = 0;
            }
            // Mobile/Stack: Scroll section into view
            if (topRef.current) {
                topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            shouldScrollToTop.current = false;
        }
    }, [assignments]);

    useEffect(() => {
        if (open && user) {
            setAssignments([]);
            setGroups([]);
            setAppTables([]);
            setLoading(true);
            fetchData();
            resetForm();
        }
    }, [open, applicationId, user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [assignResp, groupsResp, tablesResp] = await Promise.all([
                apiHelper.apiCall(`/arcee/group/assign/list/?user_id=${user.id}&application_id=${applicationId}`),
                apiHelper.apiCall(`/arcee/groups/?application_id=${applicationId}`),
                apiHelper.apiCall(`/arcee/application/table/list/?application_id=${applicationId}&page_size=100`) // Fetch all tables
            ]);

            if (assignResp.ok && groupsResp.ok && tablesResp.ok) {
                const aData = await assignResp.json();
                // API returns { assignments: [...] } or paginated { results: [...] } or plain array
                setAssignments(aData.assignments || aData.results || (Array.isArray(aData) ? aData : []));

                const gData = await groupsResp.json();
                setGroups(Array.isArray(gData) ? gData : (gData.results || []));

                const tData = await tablesResp.json();
                setAppTables(Array.isArray(tData) ? tData : (tData.results || []));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedGroup('');
        setSelectedScope('SELF');
        setAppliesToAll(true);
        setAllowedTables([]);
        setDiscardedTables([]);
        setEditingId(null);
    };

    const handleEdit = (assign) => {
        setEditingId(assign.id);
        setSelectedScope(assign.rls_scope);
        setAppliesToAll(assign.applies_to_all_tables);
        setAllowedTables(assign.allowed_tables || []);
        setDiscardedTables(assign.discarded_tables || []);

        // Try to find the group ID.
        const gId = assign.org_group || assign.org_group_id;
        if (gId) {
            setSelectedGroup(gId);
        } else if (assign.group_name) {
            const g = groups.find(x => x.name === assign.group_name);
            if (g) setSelectedGroup(g.id);
        } else {
            setSelectedGroup('');
        }
    };

    const handleAddAssignment = async () => {
        // Validation
        if (selectedScope !== 'GLOBAL' && !selectedGroup) {
            showAlertDialog({ title: 'Validation', message: 'Group is required for non-Global scope.', type: 'warning' });
            return;
        }

        // Check existing Global
        const hasGlobal = assignments.some(a => a.rls_scope === 'GLOBAL' && a.id !== editingId);
        if (hasGlobal || (selectedScope === 'GLOBAL' && assignments.length > 0 && !editingId)) {
            showAlertDialog({ title: 'Conflict', message: 'Global scope cannot coexist with other assignments.', type: 'error' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                user_id: user.id,
                application_id: applicationId,
                org_group_id: selectedScope === 'GLOBAL' ? null : (selectedGroup || null),
                rls_scope: selectedScope,
                applies_to_all_tables: appliesToAll,
                allowed_table_ids: allowedTables,
                discarded_table_ids: discardedTables
            };

            if (editingId) {
                payload.assignment_id = editingId;
            }

            const resp = await apiHelper.apiCall('/arcee/group/assign/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                const err = await resp.json();
                throw new Error(err.error || err.message || 'Failed to save assignment');
            }

            resetForm();
            shouldScrollToTop.current = true;
            await fetchData(); // Refresh list

        } catch (e) {
            showAlertDialog({ title: 'Error', message: e.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (assignmentId) => {
        showAlertDialog({
            title: 'Delete Assignment',
            message: 'Are you sure you want to delete this assignment rule?',
            type: 'warning',
            okLabel: 'Delete',
            showCancel: true,
            onOk: async () => {
                setLoading(true);
                try {
                    const resp = await apiHelper.apiCall('/arcee/group/assign/delete/', {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: assignmentId })
                    });
                    if (!resp.ok) {
                        throw new Error('Failed to delete assignment');
                    }
                    fetchData();
                } catch (e) {
                    showAlertDialog({ title: 'Error', message: e.message, type: 'error' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const toggleTable = (id, list, setList) => {
        if (list.includes(id)) {
            setList(list.filter(x => x !== id));
        } else {
            setList([...list, id]);
        }
    };

    if (!open) return null;

    return (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '1140px' }}>
                <div className="modal-content shadow-lg border-0 overflow-hidden" style={{ borderRadius: theme.cardRadius, height: '90vh', maxHeight: '100%' }}>
                    <div className="modal-header border-bottom py-3 px-4 bg-white">
                        <div>
                            <h5 className="modal-title fw-bold">Assignment Rules</h5>
                            <div className="text-muted small">Manage access for <span className="fw-bold text-dark">{user?.username}</span></div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-light rounded-circle p-0 text-muted shadow-sm"
                            onClick={onClose}
                            style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                backgroundColor: '#f8f9fa'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.classList.remove('text-muted');
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.color = '';
                                e.currentTarget.classList.add('text-muted');
                            }}
                        >
                            <i className="bi bi-x-lg" style={{ fontSize: '1rem' }}></i>
                        </button>
                    </div>

                    <div className="modal-body p-0 row g-0 h-auto h-md-100">
                        <div className="col-12 col-md-8 h-auto h-md-100 d-flex flex-column bg-white order-1 order-md-1 border-end-md">
                            <div className="p-3 p-md-4 border-bottom bg-light" ref={topRef}>
                                <h6 className="fw-bold mb-0 text-dark">Current Rules</h6>
                                <small className="text-muted">Active access configurations</small>
                            </div>

                            <div className="p-3 p-md-4 overflow-auto flex-grow-1" style={{ minHeight: '300px' }} ref={listContainerRef}>
                                {loading && assignments.length === 0 && <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></div>}

                                {!loading && assignments.length === 0 && (
                                    <div className="text-center py-5 text-muted">
                                        <i className="bi bi-clipboard-x fs-1 opacity-25 mb-2 d-block"></i>
                                        No active rules found.
                                        <div className="mt-3 d-md-none">
                                            <button
                                                className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                                onClick={() => formSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i>
                                                Add New Rule
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex flex-column gap-3">
                                    {assignments.map(assign => (
                                        <div key={assign.id} className="card border-0 shadow-sm transition-all hover-transform">
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        {assign.rls_scope === 'GLOBAL' ? (
                                                            <div className="fw-bold text-danger mb-1 d-flex align-items-center">
                                                                <i className="bi bi-globe me-2 fs-5"></i>
                                                                <span>GLOBAL ACCESS</span>
                                                            </div>
                                                        ) : (
                                                            <div className="fw-bold text-dark mb-1 d-flex align-items-center">
                                                                <div className="bg-light rounded-circle p-1 me-2"><i className="bi bi-people text-primary"></i></div>
                                                                <div>
                                                                    {assign.group_name}
                                                                    <div className="text-muted fw-normal" style={{ fontSize: '0.75rem' }}>Type: {assign.group_type}</div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="mt-2 ps-1 border-start border-2 ms-1">
                                                            <div className="d-flex flex-wrap gap-2 mb-1 ps-2">
                                                                <span className="badge bg-light text-dark border">{assign.rls_scope} Scope</span>
                                                            </div>
                                                            <div className="ps-2 small">
                                                                {assign.applies_to_all_tables ? (
                                                                    <span className="text-success"><i className="bi bi-check-circle-fill me-1"></i>All Tables Access</span>
                                                                ) : (
                                                                    <span className="text-warning text-dark"><i className="bi bi-exclamation-circle-fill me-1"></i>Specific Tables ({assign.allowed_tables?.length || 0})</span>
                                                                )}
                                                                {assign.discarded_tables?.length > 0 && <div className="text-danger mt-1"><i className="bi bi-ban me-1"></i>{assign.discarded_tables.length} Exceptions</div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        <button
                                                            className="btn btn-light btn-sm text-dark rounded-circle shadow-sm"
                                                            onClick={() => handleEdit(assign)}
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            title="Edit Rule"
                                                        >
                                                            <i className="bi bi-pencil-fill" style={{ fontSize: '0.8rem' }}></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-light btn-sm text-danger rounded-circle shadow-sm"
                                                            onClick={() => handleDelete(assign.id)}
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={e => {
                                                                e.currentTarget.style.backgroundColor = '#dc3545';
                                                                e.currentTarget.style.color = '#ffffff';
                                                                e.currentTarget.classList.remove('text-danger');
                                                            }}
                                                            onMouseLeave={e => {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.color = '';
                                                                e.currentTarget.classList.add('text-danger');
                                                            }}
                                                        >
                                                            <i className="bi bi-trash-fill" style={{ fontSize: '0.8rem' }}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Panel: Form */}
                        <div className="col-12 col-md-4 h-auto h-md-100 d-flex flex-column bg-surface fs-md-6 flex-shrink-0 order-2 order-md-2" style={{ background: '#fcfcfc' }}>
                            <div className="p-3 p-md-4 border-bottom bg-white d-flex align-items-center justify-content-between" ref={formSectionRef}>
                                <div>
                                    <h6 className="fw-bold mb-0 text-primary">{editingId ? 'Edit Rule' : 'Add New Rule'}</h6>
                                    <small className="text-muted">{editingId ? 'Modify existing permissions' : 'Grant access to specific data'}</small>
                                </div>
                                {editingId && (
                                    <button
                                        className="btn btn-sm btn-light border shadow-sm rounded-pill px-3 d-flex align-items-center gap-2 transition-all"
                                        onClick={resetForm}
                                        title="Exit Edit Mode"
                                        style={{ backgroundColor: '#fff' }}
                                    >
                                        <i className="bi bi-x-circle text-danger"></i>
                                        <span className="fw-semibold text-dark small">Cancel Edit</span>
                                    </button>
                                )}
                            </div>

                            <div className="p-3 p-md-4 overflow-auto flex-grow-1 pb-5">
                                <div className="alert alert-info small border-0 bg-light-info text-dark mb-4" style={{ backgroundColor: '#e2f2ff' }}>
                                    <div className="d-flex">
                                        <i className="bi bi-info-circle-fill text-info me-2 mt-1"></i>
                                        <div>
                                            <strong>Access Control Layers:</strong><br />
                                            1. <strong>RLS (Group Assign)</strong>: Controls <em>which tables they have access to and which records</em> a user sees.<br />
                                            2. <strong>Table Permissions</strong>: Controls <em>actions</em> (Read, Write) on those tables and records.<br />
                                            <span className="text-decoration-underline">Both are required for access.</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-uppercase text-muted">Scope Level</label>
                                    <select
                                        className="form-select shadow-sm border-0 bg-white"
                                        value={selectedScope}
                                        onChange={e => setSelectedScope(e.target.value)}
                                        style={{ height: '3rem', borderRadius: theme.inputRadius }}
                                    >
                                        {SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>

                                {selectedScope !== 'GLOBAL' && (
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-uppercase text-muted">Organization Group</label>
                                        <select
                                            className="form-select shadow-sm border-0 bg-white"
                                            value={selectedGroup}
                                            onChange={e => {
                                                const gId = parseInt(e.target.value);
                                                setSelectedGroup(gId);
                                                const grp = groups.find(g => g.id === gId);
                                                if (grp && grp.default_tables) {
                                                    setAppliesToAll(false);
                                                    setAllowedTables(grp.default_tables);
                                                } else {
                                                    setAllowedTables([]);
                                                }
                                            }}
                                            style={{ height: '3rem', borderRadius: theme.inputRadius }}
                                        >
                                            <option value="">Select Group...</option>
                                            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </select>
                                    </div>
                                )}

                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-uppercase text-muted mb-2">Table Access</label>
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-white border-0 py-3">
                                            <div className="d-flex gap-3">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        checked={appliesToAll}
                                                        onChange={() => { setAppliesToAll(true); setAllowedTables([]); }}
                                                    />
                                                    <label className="form-check-label small fw-bold">All Tables</label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        checked={!appliesToAll}
                                                        onChange={() => { setAppliesToAll(false); setDiscardedTables([]); }}
                                                    />
                                                    <label className="form-check-label small fw-bold">Specific</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card-body p-0 border-top">
                                            <div className="p-2 border-bottom">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-light border-end-0"><i className="bi bi-search text-muted"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control border-start-0 bg-light shadow-none"
                                                        placeholder="Search tables..."
                                                        value={tableSearch}
                                                        onChange={e => setTableSearch(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="bg-light p-2 small text-center text-muted border-bottom">
                                                {appliesToAll ? 'Select exceptions (Deny List)' : 'Select Allowed Tables'}
                                            </div>
                                            <div className="p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                {appTables
                                                    .filter(t =>
                                                        (t.display_name || '').toLowerCase().includes(tableSearch.toLowerCase()) ||
                                                        (t.name || '').toLowerCase().includes(tableSearch.toLowerCase())
                                                    )
                                                    .sort((a, b) => {
                                                        const isCheckedA = appliesToAll
                                                            ? discardedTables.includes(a.id)
                                                            : allowedTables.includes(a.id);
                                                        const isCheckedB = appliesToAll
                                                            ? discardedTables.includes(b.id)
                                                            : allowedTables.includes(b.id);

                                                        if (isCheckedA === isCheckedB) return (a.display_name || a.name).localeCompare(b.display_name || b.name);
                                                        return isCheckedA ? -1 : 1;
                                                    })
                                                    .map(t => {
                                                        const isChecked = appliesToAll
                                                            ? discardedTables.includes(t.id)
                                                            : allowedTables.includes(t.id);

                                                        const toggle = () => appliesToAll
                                                            ? toggleTable(t.id, discardedTables, setDiscardedTables)
                                                            : toggleTable(t.id, allowedTables, setAllowedTables);

                                                        return (
                                                            <div key={t.id} className={`form-check py-1 hover-bg-light rounded ps-4 pe-2 ${isChecked ? 'bg-white shadow-sm border mb-1' : ''}`}>
                                                                <input
                                                                    className="form-check-input mt-1"
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={toggle}
                                                                />
                                                                <label className="form-check-label small w-100 text-break" onClick={toggle} style={{ cursor: 'pointer' }}>
                                                                    <span className={isChecked ? 'fw-bold text-primary' : 'fw-semibold'}>{t.display_name}</span> <span className="text-muted">({t.name})</span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                {appTables
                                                    .filter(t =>
                                                        (t.display_name || '').toLowerCase().includes(tableSearch.toLowerCase()) ||
                                                        (t.name || '').toLowerCase().includes(tableSearch.toLowerCase())
                                                    ).length === 0 && appTables.length > 0 && (
                                                        <div className="text-center py-3 text-muted fst-italic small">No matching tables found</div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    className="btn btn-primary w-100 py-3 shadow-sm fw-bold mb-3"
                                    disabled={loading}
                                    onClick={handleAddAssignment}
                                    style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                                >
                                    {loading ? 'Processing...' : (editingId ? 'Update Assignment Rule' : 'Add Assignment Rule')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
